// hooks.ts
import { getAccount } from "queries/account";
import { Account } from "queries/account/types";
import { MembershipStatus, User } from "queries/current-user/types";
import { toast } from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { captureUserVisibleError } from "ui/lib/sentry";
import { isSessionExpiredError } from "queries/lib/rocky-api-error-handler";
import {
  createDMRoom,
  createRoom,
  getRoom,
  joinRoom,
  leaveRoom,
  listCuratedRooms,
  listRoomActionsV2,
  listRooms,
  updateLegacyRoom,
  updateRoom,
  updateUserRoomMembership,
} from "./services";
import {
  CreateDMRoomResponse,
  LegacyRoom,
  ListRoomsResponse,
  Room,
  RoomResponse,
} from "./types";

/** Match the rocky error returned when a user has no membership for a room. */
const isNoMembershipError = (error: unknown): boolean => {
  const message =
    (error as any)?.response?.data?.errors?.[0]?.message ??
    (error as any)?.message ??
    "";
  return (
    typeof message === "string" &&
    /does not have membership to room/i.test(message)
  );
};

const useRoomMutations = () => {
  const queryClient = useQueryClient();

  const createRoomMutation = useMutation<Room, Error, Partial<Room>>(
    async (data: Partial<Room>) => {
      const response = await createRoom(data);
      return response;
    },
    {
      onSuccess: (createdRoom) => {
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success(`Room created`);
      },
      onError: (error: Error, variables) => {
        console.error("Error creating room:", error);
        toast.error(`Something went wrong`);
        captureUserVisibleError(error, {
          surface: "chat_room_create",
          extra: {
            roomType: variables.type,
            roomName: variables.name,
            fields: Object.keys(variables),
          },
        });
      },
    }
  );

  const createDMRoomMutation = useMutation<
    CreateDMRoomResponse,
    Error,
    { invitedUserIDs: string[] }
  >(
    async (data: { invitedUserIDs: string[] }) => {
      const response = await createDMRoom(data);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success("DM room created successfully");
      },
      onError: (error: Error, variables) => {
        console.error("Error creating DM room:", error);
        toast.error("Failed to create DM room");
        captureUserVisibleError(error, {
          surface: "chat_dm_create",
          extra: { invitedUserIDs: variables.invitedUserIDs },
        });
      },
    }
  );

  const joinRoomMutation = useMutation(
    async (roomID: string) => {
      const response = await joinRoom(roomID);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success("Joined room successfully");
      },
      onError: (error: Error, roomID) => {
        console.error("Error joining room:", error);
        toast.error("Failed to join room");
        captureUserVisibleError(error, {
          surface: "chat_room_join",
          extra: { roomID },
        });
      },
    }
  );

  const updateRoomMutation = useMutation(
    (data: { roomID: string; roomData: Partial<Room> }) =>
      updateRoom(data.roomID, data.roomData),
    {
      onMutate: async (variables) => {
        // Snapshot the previous value
        const previousRoom = queryClient.getQueryData<Room>([
          `room`,
          variables.roomID,
        ]);

        // Optimistically update the state
        queryClient.setQueryData<Room>(
          [`room`, variables.roomID],
          (previousRoom) => {
            if (!previousRoom) {
              return { ...variables.roomData } as Room;
            }
            return {
              ...previousRoom,
              ...variables.roomData,
            };
          }
        );

        return { previousRoom };
      },
      onSuccess: () => {
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success(`Room updated`);
      },
      onError: (error: Error, variables, context: any) => {
        console.error("Error updating room:", error);
        toast.error(`Something went wrong`);
        captureUserVisibleError(error, {
          surface: "chat_room_update",
          extra: { roomID: variables.roomID, fields: Object.keys(variables.roomData) },
        });
        if (context?.previousRoom) {
          queryClient.setQueryData<Room>(
            [`room`, variables.roomID],
            context.previousRoom
          );
        }
      },
    }
  );

  const updateLegacyRoomMutation = useMutation(
    (data: { roomID: string; roomData: Partial<LegacyRoom> }) =>
      updateLegacyRoom(data.roomID, data.roomData),
    {
      onMutate: async (variables) => {
        // Snapshot the previous value
        const previousRoom = queryClient.getQueryData<LegacyRoom>([
          `legacy-room`,
          variables.roomID,
        ]);

        // Optimistically update the state
        queryClient.setQueryData<Room>(
          [`legacy-room`, variables.roomID],
          (previousRoom) => {
            if (!previousRoom) {
              return { ...variables.roomData } as Room;
            }
            return {
              ...previousRoom,
              ...variables.roomData,
            };
          }
        );

        return { previousRoom };
      },
      onSuccess: () => {
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success(`Room updated`);
      },
      onError: (error: Error, variables, context: any) => {
        console.error("Error updating legacy room:", error);
        toast.error(`Something went wrong`);
        captureUserVisibleError(error, {
          surface: "chat_legacy_room_update",
          extra: { roomID: variables.roomID, fields: Object.keys(variables.roomData) },
        });
        if (context?.previousRoom) {
          queryClient.setQueryData<LegacyRoom>(
            [`legacy-room`, variables.roomID],
            context.previousRoom
          );
        }
      },
    }
  );

  const updateUserRoomMembershipMutation = useMutation(
    (data: {
      roomID: string;
      userHasAcknowledged?: true;
      roomIsHidden?: true;
      lastMessageReadTSStr?: Date;
    }) => updateUserRoomMembership(data),
    {
      onSuccess: () => {
        // Intentionally NOT invalidating "listRooms" here. This mutation
        // fires on every active-room change to mark the room as read; an
        // invalidation would refetch the whole list on every room click.
        // Real-time unread state is handled separately via PubNub.
        queryClient.invalidateQueries("curatedRooms");
      },
      onError: (error: Error, variables) => {
        // "No membership" is a benign race: the user just left the room
        // and a stale list entry triggered this mark-as-read attempt.
        // Don't toast/capture — the user already saw the leave succeed.
        if (isNoMembershipError(error)) {
          console.warn(
            "Skipping room membership update for non-member room:",
            variables.roomID
          );
          return;
        }
        // A genuinely expired session on this silent background "mark as
        // read" ping isn't actionable here — the user didn't trigger this
        // call, so there's nothing to retry or surface. They'll hit the
        // real re-auth prompt on their next user-initiated action.
        if (isSessionExpiredError(error)) {
          console.warn(
            "Skipping room membership update after session expiry:",
            variables.roomID
          );
          return;
        }
        console.error("Error updating room membership:", error);
        toast.error("Failed to update room membership");
        captureUserVisibleError(error, {
          surface: "chat_room_membership_update",
          extra: { roomID: variables.roomID },
        });
      },
    }
  );

  const leaveRoomMutation = useMutation(
    async (roomID: string) => {
      await leaveRoom(roomID);
    },
    {
      onSuccess: (_data, roomID) => {
        // Optimistically remove the user's membership from the listRooms
        // cache so getAuthUserRoomMembershipsByRoomID returns falsy
        // synchronously. This prevents a race where the mark-as-read
        // effect fires for the just-left room before the invalidation
        // refetch completes. The room object itself stays in `rooms` so
        // it can re-appear under "Explore Communities".
        queryClient.setQueryData<ListRoomsResponse | undefined>(
          "listRooms",
          (current) => {
            if (!current) return current;
            return {
              ...current,
              authUserRoomMemberships:
                current.authUserRoomMemberships?.filter(
                  (m) => m.roomID !== roomID
                ) ?? [],
            };
          }
        );
        queryClient.invalidateQueries("curatedRooms");
        queryClient.invalidateQueries("listRooms");
        toast.success("Left room successfully");
      },
      onError: (error: Error, roomID) => {
        console.error("Error leaving room:", error);
        toast.error("Failed to leave room");
        captureUserVisibleError(error, {
          surface: "chat_room_leave",
          extra: { roomID },
        });
      },
    }
  );

  return {
    createRoom: createRoomMutation,
    createDMRoom: createDMRoomMutation,
    joinRoom: joinRoomMutation,
    updateRoom: updateRoomMutation,
    updateLegacyRoom: updateLegacyRoomMutation,
    updateUserRoomMembership: updateUserRoomMembershipMutation,
    leaveRoom: leaveRoomMutation,
  };
};

const useListRooms = () => {
  const { data, error, isLoading, refetch } = useQuery("listRooms", listRooms);

  return {
    data,
    error,
    isLoading,
    refetch,
  };
};

const useCuratedRooms = () => {
  const { data, error, isLoading, refetch } = useQuery(
    "curatedRooms",
    listCuratedRooms
  );

  return {
    data,
    error,
    isLoading,
    refetch,
  };
};

const useRoom = (roomID: string) => {
  const { data, error, isLoading } = useQuery(
    ["room", roomID],
    () => getRoom(roomID),
    {
      enabled: !!roomID,
    }
  );

  return {
    data,
    error,
    isLoading,
  };
};

// @deprecated Wraps the deprecated `listRoomActionsV2` endpoint — see
// `services.ts` for context. Mention pickers use the local store now.
const useRoomActions = (
  roomID: string,
  searchStr?: string,
  enabled: boolean = true
) => {
  const { data, error, isLoading } = useQuery(
    ["roomActionsV2", roomID, searchStr],
    () => listRoomActionsV2(roomID, searchStr),
    {
      enabled,
    }
  );

  return {
    data,
    error,
    isLoading,
  };
};

export interface RoomWithCreator extends Room {
  user?: User;
}

export interface RoomAndUserResponse extends Omit<RoomResponse, "room"> {
  room: RoomWithCreator;
}

const transformUser = (account: Account): Partial<User> => {
  return {
    id: account.user.id,
    role: account.user.roleName,
    profileImg: account.user.profileImgImgixURL,
    profileImgImgixURL: account.user.profileImgImgixURL,
    givenName: account.user.givenName,
    familyName: account.user.familyName,
    workoutCompletions: account.userProfile.workoutCompletionCount,
    activeSubType: account.primarySubscription?.status,
    membershipStatus: account.membershipStatus as MembershipStatus,
  };
};

const useRoomAndUser = (roomID: string, initialData?: any) => {
  const {
    data: roomData,
    error: roomError,
    isLoading: isRoomLoading,
  } = useQuery(["room", roomID], () => getRoom(roomID), {
    enabled: !!roomID,
    initialData: initialData?.room ? initialData.room : undefined,
  });

  const {
    data: userData,
    error: userError,
    isLoading: isUserLoading,
  } = useQuery(
    ["user-account", roomData?.room.creatorUserID],
    () => getAccount(roomData!.room.creatorUserID),
    {
      enabled:
        !!roomData?.room.creatorUserID && roomData?.room?.type === "COACH",
      initialData: initialData?.room?.user ? initialData.room.user : undefined,
    }
  );

  const data: RoomAndUserResponse | undefined = roomData
    ? {
        ...roomData,
        room: {
          ...roomData.room,
          user:
            roomData.room.type === "COACH" && userData
              ? transformUser(userData)
              : undefined,
        },
      }
    : undefined;

  const error = roomError || userError;
  const isLoading = isRoomLoading || isUserLoading;

  return {
    data,
    error,
    isLoading,
  };
};

export {
  useCuratedRooms,
  useListRooms,
  useRoom,
  useRoomActions,
  useRoomAndUser,
  useRoomMutations,
};
